import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { App as AntdApp, Descriptions, Card, Space, Button, Typography, Tag, Spin, Steps, Modal, Input, Alert, Upload } from 'antd'
import { ArrowLeftOutlined, SyncOutlined, CopyOutlined, PaperClipOutlined, UploadOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'
import { useTicketDetail, useSyncTicket, useCustomerConfirm, useCustomerReturn } from '@/modules/ticket/queries'
import { useQueueMessageMediaDownload } from '@/modules/message/queries'
import { useAuthStore } from '@/stores/authStore'
import { getSlaRuleName } from '@/modules/sla/labels'
import ErrorResult from '@/components/feedback/ErrorResult'
import WechatConversation from '@/pages/messages/components/WechatConversation'
import { useState } from 'react'

const { Text, Paragraph } = Typography
const { TextArea } = Input
const { Dragger } = Upload

export default function TicketDetailPage() {
  const { message } = AntdApp.useApp()
  const { mappingId } = useParams<{ mappingId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo
  const { data, isLoading, isError, error, refetch } = useTicketDetail(mappingId || null)
  const hasPermission = useAuthStore((s) => s.hasPermission)

  const syncTicket = useSyncTicket()
  const customerConfirm = useCustomerConfirm()
  const customerReturn = useCustomerReturn()
  const queueMediaDownload = useQueueMessageMediaDownload()
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [returnModalOpen, setReturnModalOpen] = useState(false)
  const [confirmNote, setConfirmNote] = useState('')
  const [confirmFiles, setConfirmFiles] = useState<UploadFile[]>([])
  const [returnReason, setReturnReason] = useState('')

  // 从 operator_actions 读取操作状态
  const confirmAction = data?.operator_actions?.find((a) => a.action === 'customer-confirm')
  const returnAction = data?.operator_actions?.find((a) => a.action === 'customer-return')
  const syncAction = data?.operator_actions?.find((a) => a.action === 'sync')
  const canCustomerConfirm = confirmAction?.enabled === true && data?.platform_status === 'waiting_customer_confirm'
  const canCustomerReturn = returnAction?.enabled === true && data?.platform_status === 'waiting_customer_confirm'

  if (isLoading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
  if (isError || !data) return <ErrorResult error={error} onRetry={() => refetch()} />

  const platformStatusSteps = ['received', 'draft_confirming', 'waiting_start', 'doing', 'waiting_customer_confirm', 'closed']
  const rawStep = platformStatusSteps.indexOf(data.platform_status)
  const currentStep = rawStep >= 0 ? rawStep : 0
  const isUnknownStatus = rawStep < 0
  const confirmationAttachments = data.customer_confirmation_attachments || []

  // 复制话术到剪贴板
  const copyScript = (text: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
    }
  }

  return (
    <div data-testid="ticket-detail-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space>
        <Button data-testid="ticket-back-btn" icon={<ArrowLeftOutlined />} onClick={() => navigate(returnTo || '/tickets')}>返回列表</Button>
        <Text strong style={{ fontSize: 16 }}>task#{data.zentao_task_id} {data.task_title}</Text>
        {syncAction?.enabled !== false && hasPermission('ticket:sync') && (
          <Button icon={<SyncOutlined />} loading={syncTicket.isPending}
            onClick={() => syncTicket.mutate(data.zentao_task_id)}>手动同步</Button>
        )}
      </Space>

      {/* 状态链 */}
      <Card size="small" title="平台状态链">
        <Steps
          size="small"
          current={isUnknownStatus ? undefined : currentStep}
          status={data.platform_status === 'returned' ? 'error' : 'process'}
          items={[
            { title: '已接收' },
            { title: '草稿确认' },
            { title: '待接单' },
            { title: '处理中' },
            { title: '待确认' },
            { title: '已关闭' },
          ]}
        />
        <Space style={{ marginTop: 8 }}>
          <Text type="secondary">当前状态:</Text>
          <Tag color={isUnknownStatus ? 'default' : 'blue'}>{data.status_label || data.platform_status}</Tag>
          {data.next_action_hint && <Text type="warning">{data.next_action_hint}</Text>}
        </Space>
      </Card>

      {/* 微信群触发来源 */}
      {data.source && (
        <Card size="small" title="触发来源（微信群）">
          <Descriptions size="small" column={2}>
            {data.source?.room_id && (
              <Descriptions.Item label="群聊ID">
                <Text code>{data.source.room_id}</Text>
              </Descriptions.Item>
            )}
            {data.source?.trigger_key && (
              <Descriptions.Item label="Trigger Key">
                <Text code>{data.source.trigger_key}</Text>
              </Descriptions.Item>
            )}
            {data.source?.trigger_message_summary && (
              <Descriptions.Item label="触发消息摘要">
                <Text>{data.source.trigger_message_summary}</Text>
              </Descriptions.Item>
            )}
            {data.source?.context_message_summary && (
              <Descriptions.Item label="合并转发摘要">
                <Text>{data.source.context_message_summary}</Text>
              </Descriptions.Item>
            )}
            {data.source?.forward_summary && (
              <Descriptions.Item label="转发摘要" span={2}>
                <Text>{data.source.forward_summary}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* 基础信息 */}
        <Card size="small" title="基础信息">
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="客户">{data.customer_name}</Descriptions.Item>
            <Descriptions.Item label="项目">{data.project_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="是否已过保">
              <Tag color={data.is_out_of_warranty ? 'red' : 'green'}>
                {data.is_out_of_warranty ? '已过保' : '未过保'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="接待负责人">{data.service_owner_name || data.service_owner || '-'}</Descriptions.Item>
            <Descriptions.Item label="第一负责人">{data.tech_owner_name || data.tech_owner || '-'}</Descriptions.Item>
            <Descriptions.Item label="当前执行人">{data.assignee_name || data.assignee || data.assigned_to || '-'}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 时间戳 */}
        <Card size="small" title="关键时间点">
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="首响时间">{data.first_response_at || '-'}</Descriptions.Item>
            <Descriptions.Item label="建单时间">{data.task_created_at || '-'}</Descriptions.Item>
            <Descriptions.Item label="开始处理">{data.task_started_at || '-'}</Descriptions.Item>
            <Descriptions.Item label="处理完成">{data.task_finished_at || '-'}</Descriptions.Item>
            <Descriptions.Item label="客户确认">{data.customer_confirmed_at || '-'}</Descriptions.Item>
          </Descriptions>
        </Card>
      </div>

      {/* 禅道 task 链接与状态 */}
      <Card size="small" title="禅道任务">
        <Descriptions size="small" column={3}>
          <Descriptions.Item label="禅道Task ID">
            {/* TODO: 等禅道URL配置化后改为可点击链接 */}
            <Text code>#{data.zentao_task_id}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="禅道状态">
            <Tag>{data.zentao_status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="当前执行人">
            <Text>{data.assignee_name || data.assignee || data.assigned_to || '-'}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 客户确认话术（waiting_customer_confirm 时展示） */}
      {data.platform_status === 'waiting_customer_confirm' && data.confirm_script && (
        <Card
          size="small"
          title="可复制话术"
          extra={
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyScript(data.confirm_script!)}
            >
              复制
            </Button>
          }
        >
          <Alert
            type="info"
            title="话术仅供复制使用，不会自动发送群消息"
            style={{ marginBottom: 8, fontSize: 12 }}
          />
          <Paragraph style={{ background: '#f6ffed', padding: 12, borderRadius: 4, fontSize: 13 }}>
            {data.confirm_script}
          </Paragraph>
        </Card>
      )}

      {(data.handoff_note || data.handoff_at) && (
        <Card size="small" title="交接记录">
          <Descriptions size="small" column={2}>
            {data.handoff_at && (
              <Descriptions.Item label="交接时间">{data.handoff_at}</Descriptions.Item>
            )}
            {data.handoff_note && (
              <Descriptions.Item label="交接说明">{data.handoff_note}</Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {/* 禅道动作历史 */}
      <Card size="small" title="动作历史">
        {data.task_status_events.length > 0 ? (
          <div style={{ borderLeft: '2px solid #d9d9d9', paddingLeft: 12 }}>
            {data.task_status_events.map((evt, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{evt.created_at} [{evt.status}]</Text>
                <br />
                <Text>{evt.note}</Text>
              </div>
            ))}
          </div>
        ) : <Text type="secondary">暂无动作记录</Text>}
      </Card>

      {/* 来源消息 */}
      {confirmationAttachments.length > 0 && (
        <Card size="small" title="客户确认附件">
          <Space direction="vertical" style={{ width: '100%' }}>
            {confirmationAttachments.map((attachment) => (
              <Space key={attachment.attachment_uid} wrap>
                <PaperClipOutlined />
                <a
                  href={attachment.download_url || `/api/tickets/${data.mapping_id}/customer-confirm-attachments/${attachment.attachment_uid}/content`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {attachment.original_name || attachment.attachment_uid}
                </a>
                <Text type="secondary">{attachment.size_bytes ? `${Math.ceil(attachment.size_bytes / 1024)} KB` : '-'}</Text>
                <Tag color={attachment.upload_status === 'uploaded' ? 'green' : attachment.upload_status === 'failed' ? 'red' : 'default'}>
                  {attachment.upload_status}
                </Tag>
                {attachment.last_error && <Text type="danger">{attachment.last_error}</Text>}
              </Space>
            ))}
          </Space>
        </Card>
      )}

      {data.source_messages.length > 0 && (
        <Card size="small" title="来源消息">
          <WechatConversation
            messages={data.source_messages}
            pendingMediaUid={
              queueMediaDownload.isPending ? queueMediaDownload.variables?.mediaUid : undefined
            }
            onQueueDownload={(messageId, mediaUid) => {
              queueMediaDownload.mutate(
                { messageId, mediaUid },
                {
                  onSuccess: () => {
                    message.success('已加入下载队列')
                    void refetch()
                  },
                },
              )
            }}
          />
        </Card>
      )}

      {/* 响应时限提醒 */}
      {data.open_sla_events.length > 0 && (
        <Card size="small" title="响应时限提醒">
          {data.open_sla_events.map((evt) => (
            <Tag key={evt.id} color="red">{getSlaRuleName(evt.rule_code, evt.rule_name)} - {evt.event_status}</Tag>
          ))}
        </Card>
      )}

      {/* 操作栏 */}
      <Card size="small">
        <Space wrap>
          <Button
            data-testid="ticket-action-view-workflow"
            onClick={() => navigate(`/workflow-traces/ticket/${data.mapping_id}`)}
          >
            查看流程
          </Button>
          {hasPermission('ticket:customer_confirm') && canCustomerConfirm && (
            <Button
              type="primary"
              onClick={() => setConfirmModalOpen(true)}
            >
              确认通过并关闭
            </Button>
          )}
          {hasPermission('ticket:customer_return') && canCustomerReturn && (
            <Button
              danger
              onClick={() => setReturnModalOpen(true)}
            >
              客户退回继续处理
            </Button>
          )}
        </Space>
      </Card>

      {/* 确认通过弹窗 */}
      <Modal title="确认通过并关闭" open={confirmModalOpen}
        onOk={() => {
          const note = confirmNote.trim()
          if (!canCustomerConfirm) {
            message.warning('当前状态不支持客户确认关闭')
            setConfirmModalOpen(false)
            return
          }
          if (!note) {
            message.warning('请填写确认备注')
            return
          }
          const files = confirmFiles
            .map((file) => file.originFileObj)
            .filter((file): file is NonNullable<UploadFile['originFileObj']> => Boolean(file))
          customerConfirm.mutate(
            { mappingId: data.mapping_id, note, files },
            {
              onSuccess: () => {
                message.success('已确认通过并关闭工单')
                setConfirmModalOpen(false)
                setConfirmNote('')
                setConfirmFiles([])
                void refetch()
              },
              onError: (err) => {
                message.error(err instanceof Error ? err.message : '确认关闭失败')
              },
            },
          )
        }}
        onCancel={() => {
          setConfirmModalOpen(false)
          setConfirmNote('')
          setConfirmFiles([])
        }}
        confirmLoading={customerConfirm.isPending}
        okButtonProps={{ disabled: confirmNote.trim().length === 0 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <TextArea placeholder="确认备注" value={confirmNote} onChange={(e) => setConfirmNote(e.target.value)} />
          <Dragger
            multiple
            fileList={confirmFiles}
            beforeUpload={() => false}
            onChange={({ fileList }) => setConfirmFiles(fileList)}
            onRemove={(file) => {
              setConfirmFiles((items) => items.filter((item) => item.uid !== file.uid))
              return true
            }}
          >
            <p className="ant-upload-drag-icon"><UploadOutlined /></p>
            <p className="ant-upload-text">上传客户确认附件/图片</p>
          </Dragger>
        </Space>
      </Modal>

      {/* 退回弹窗 */}
      <Modal title="客户退回继续处理" open={returnModalOpen}
        onOk={() => {
          const reason = returnReason.trim()
          if (!canCustomerReturn) {
            message.warning('当前状态不支持客户退回')
            setReturnModalOpen(false)
            return
          }
          if (!reason) {
            message.warning('请填写退回原因')
            return
          }
          customerReturn.mutate(
            { mappingId: data.mapping_id, reason },
            {
              onSuccess: () => {
                message.success('已退回工单，等待继续处理')
                setReturnModalOpen(false)
                setReturnReason('')
                void refetch()
              },
              onError: (err) => {
                message.error(err instanceof Error ? err.message : '退回继续处理失败')
              },
            },
          )
        }}
        onCancel={() => {
          setReturnModalOpen(false)
          setReturnReason('')
        }}
        confirmLoading={customerReturn.isPending}
        okButtonProps={{ disabled: returnReason.trim().length === 0 }}>
        <TextArea placeholder="退回原因（必填）" value={returnReason} onChange={(e) => setReturnReason(e.target.value)} />
      </Modal>
    </div>
  )
}
